// MessageThread — universal per-document message thread ("chatter").
//
// ERP UX mini-chunk. Mounted first on PurchaseOrderDetail, replacing that
// page's old header-timestamp-derived "Timeline" strip: for purchase orders
// the system entries below come from the audit spine (purchase_order_events),
// merged in server-side, so this component now carries that history AND
// human conversation in one stream. Any future document type opts in by
// passing a different entityType — this component makes no assumption about
// which one it is, all of that lives in messagesApi.ts / the backend
// allowlist.
//
// UNIVERSAL to both instances — nothing here branches on the product.
//
// Design:
//   - One chronological stream, oldest first (the API's order), newest at the
//     bottom, auto-scrolled into view like a chat thread.
//   - kind 'system' renders muted/compact: it is context, not something that
//     asks for a reply.
//   - kind 'comment' renders as a proper message: author, relative time
//     (absolute on hover), body with line breaks preserved. Body is always a
//     React text node — never dangerouslySetInnerHTML, even though the
//     backend stores it raw (messages-service.js: React's own JSX escaping is
//     the sink it is written for).
//   - @-mentions: typing "@" opens a small filtered picker (users fetched
//     from /users/active, the same endpoint POA/MentionInput.tsx already
//     uses for this exact job — NOT the admin-only /users ApprovalSettings
//     reads, which would 403 for a procurement-only, non-admin caller). The
//     picker only lists users who would actually pass the document's own
//     capability gate (MENTION_ELIGIBILITY in messagesApi.ts) — the backend
//     never DMs a mentioned user who fails that gate (the DM carries a body
//     excerpt, so notifying an ungated user would leak thread content around
//     it), so offering them here would just @ someone who hears nothing.
//     Picking a user inserts plain "@Full Name " text and adds a chip below
//     the textarea; removing the chip drops that id from the next POST but
//     deliberately leaves the already-typed text alone, since by then it is
//     just an English sentence, not a live token. (Chip-row v1, not inline
//     span tracking — see MessageThread.test.tsx header comment for why.)

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMessagesApi, MENTION_ELIGIBILITY, type MessageItem, type ActiveUser } from './messagesApi';
import { parseTimestamp, getFullDateTime } from '../../utils/date-utils';
import { useApi } from '../../apiHelpers';
import { liveScope, useLiveScopes } from '../../live/useLiveScopes';
import useDesktop from '../../utils/useDesktop';
import { Alert } from '../ui/Alert';
import { EmptyState as KitEmptyState } from '../ui/EmptyState';
import { Skeleton, SkeletonGroup } from '../ui/Skeleton';
import Avatar from '../ui/Avatar';
import { Button, IconButton } from '../ui/Button';
import { TextArea } from '../ui/Field';
import Tag from '../ui/Tag';
import Reveal from '../ui/Reveal';
import Appear from '../ui/Appear';
import FadeIn from '../ui/FadeIn';
import { IconClose } from '../ui/icons';
import { FOCUS, MS, PRESS, usePresence } from '../ui/motion';

export interface MessageThreadProps {
  entityType: string;
  entityId: string | number;
}

interface PendingMention {
  id: string;
  name: string;
}

const CHANNEL_LABELS: Record<string, string> = {
  slack: 'Slack',
  import: 'Import',
  system: 'System',
};

function formatChannel(channel: string): string {
  return CHANNEL_LABELS[channel] || channel.charAt(0).toUpperCase() + channel.slice(1);
}

/** Relative time with the absolute stamp on hover — always relative (no
 * "falls back to a bare date after 24h" cutoff), matching a chat thread's
 * convention rather than the dashboard "last seen" one. */
const RelativeTime: React.FC<{ iso: string }> = ({ iso }) => {
  const d = parseTimestamp(iso);
  if (!d) return null;
  return (
    <span className="tabular-nums" title={getFullDateTime(iso)}>
      {d.fromNow()}
    </span>
  );
};

const SystemEntry: React.FC<{ item: MessageItem }> = ({ item }) => (
  <div
    data-testid="message-system"
    className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 py-1.5 pl-[2.125rem] text-xs leading-4 text-on-surface-variant"
  >
    <RelativeTime iso={item.created_at} />
    <span className="text-on-surface-variant">{item.body}</span>
    <span className="text-on-surface-variant">by {item.user_name ?? 'System'}</span>
    {item.channel && (
      <Tag size="sm">{formatChannel(item.channel)}</Tag>
    )}
  </div>
);

const CommentEntry: React.FC<{ item: MessageItem }> = ({ item }) => (
  <div
    data-testid="message-comment"
    // A comment merged in from a related document gets a left rule, so a tour
    // thread reads as "the tour, plus its shows" rather than one flat pile.
    className={`flex gap-2.5 py-2.5 ${item.source_label ? 'border-l-2 border-l-primary/30 pl-2.5' : ''}`}
  >
    <Avatar
      name={item.user_name ?? 'Former user'}
      color={item.avatar_color}
      shape={item.avatar_shape}
      accessory={item.avatar_accessory}
      decorative
      ring="ring-transparent"
      className="mt-0.5"
    />
    <div className="min-w-0 flex-1">
      <div className="flex flex-wrap items-baseline gap-2">
        <span className="text-sm font-medium text-on-surface">{item.user_name ?? 'Former user'}</span>
        {item.source_label && (
          <Tag data-testid="message-source-label" tone="accent" size="sm">
            {item.source_label}
          </Tag>
        )}
        <span className="text-xs text-on-surface-variant">
          <RelativeTime iso={item.created_at} />
        </span>
      </div>
      <p className="m-0 mt-0.5 text-sm text-on-surface whitespace-pre-wrap break-words">{item.body}</p>
    </div>
  </div>
);

const NoComments: React.FC = () => (
  <KitEmptyState variant="line" dense className="py-6">
    No comments yet. Anything worth noting about this record lands here.
  </KitEmptyState>
);

const MessageThread: React.FC<MessageThreadProps> = ({ entityType, entityId }) => {
  const messagesApi = useMessagesApi();
  const usersApi = useApi();

  const [items, setItems] = useState<MessageItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [draft, setDraft] = useState('');
  const [mentions, setMentions] = useState<PendingMention[]>([]);
  const [posting, setPosting] = useState(false);
  const [postError, setPostError] = useState<string | null>(null);

  // Mention popover state
  const [users, setUsers] = useState<ActiveUser[]>([]);
  const [usersLoaded, setUsersLoaded] = useState(false);
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [filter, setFilter] = useState('');
  const [highlight, setHighlight] = useState(0);
  const [tokenStart, setTokenStart] = useState<number | null>(null);

  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);

  // Small-screen presentation: the thread lives behind a floating bubble and
  // opens as a full-height sheet. Desktop ignores both.
  const isDesktop = useDesktop();
  const [sheetOpen, setSheetOpen] = useState(false);
  const sheet = usePresence(sheetOpen && !isDesktop, MS.exit);

  // Esc closes the sheet, and the sheet closes itself if the viewport grows into
  // desktop mid-session (rotate a tablet) so it cannot linger over the inline copy.
  useEffect(() => {
    if (!sheetOpen) return;
    if (isDesktop) { setSheetOpen(false); return; }
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setSheetOpen(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [sheetOpen, isDesktop]);

  // Load the thread. entityType/entityId only — the api object itself is a
  // fresh closure every render (see web/CLAUDE.md useEffect-deps note), so it
  // is deliberately left out of the dependency array rather than causing a
  // refetch loop.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    setDraft('');
    setMentions([]);
    setPostError(null);

    messagesApi
      .list(entityType, entityId)
      .then((res) => {
        if (!cancelled) setItems(res.items || []);
      })
      .catch((err: any) => {
        if (!cancelled) {
          setLoadError(err?.data?.error?.message || err?.message || 'Could not load this thread.');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityType, entityId]);

  // Other people's comments land on their own (docs/features/live-data.md). An unchanged thread
  // is left alone, and a reader scrolled up into the history stays where they are.
  const follow = useRef(true);
  const itemsNow = useRef(items);
  itemsNow.current = items;
  const thread = `${entityType}:${entityId}`;
  const threadNow = useRef(thread);
  threadNow.current = thread;
  // A failed refetch leaves the thread as it is; the rejection tells the poller to try again.
  const { holdWhile } = useLiveScopes([liveScope.messages(entityType, entityId)], (_changed, { stale }) => {
    return messagesApi
      .list(entityType, entityId)
      .then((res) => {
        const next = res.items || [];
        if (stale() || threadNow.current !== thread) return;
        setLoadError(null);
        if (JSON.stringify(next) === JSON.stringify(itemsNow.current)) return;
        const el = scrollRef.current;
        follow.current = !el || el.scrollHeight - el.scrollTop - el.clientHeight < 48;
        setItems(next);
      });
  });

  // Auto-scroll the thread's own scroll container to the bottom on initial
  // load and every time an item is appended (after posting) — never the page.
  useEffect(() => {
    const el = scrollRef.current;
    if (el && follow.current) el.scrollTop = el.scrollHeight;
    follow.current = true;
  }, [items]);

  const ensureUsers = useCallback(async () => {
    if (usersLoaded) return;
    try {
      const data: ActiveUser[] = await usersApi.get('/users/active', { silent: true });
      // A missing predicate means this entityType isn't in the map yet — show
      // everyone rather than silently emptying the picker (see the doc
      // comment on MENTION_ELIGIBILITY: an omission forfeits the courtesy, it
      // never widens who is actually notified).
      const eligible = MENTION_ELIGIBILITY[entityType];
      setUsers(eligible ? (data || []).filter(eligible) : data || []);
    } catch {
      setUsers([]);
    } finally {
      setUsersLoaded(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [usersLoaded, entityType]);

  // Detect the @-token under the cursor, same walk-back-from-cursor approach
  // as POA/MentionInput.tsx, adapted for plain-text insertion (no markdown
  // brackets to guard against here).
  const refreshTokenFromCursor = useCallback(() => {
    const ta = textareaRef.current;
    if (!ta) return;
    const cursor = ta.selectionStart ?? 0;
    let i = cursor - 1;
    while (i >= 0) {
      const ch = draft[i];
      if (ch === '@') {
        const prev = i === 0 ? '' : draft[i - 1];
        if (i === 0 || /\s/.test(prev)) {
          const tokenText = draft.slice(i + 1, cursor);
          if (/\s/.test(tokenText)) {
            setPopoverOpen(false);
            setTokenStart(null);
            return;
          }
          ensureUsers();
          setTokenStart(i);
          setFilter(tokenText.toLowerCase());
          setHighlight(0);
          setPopoverOpen(true);
          return;
        }
        break; // bare @ mid-word — not a mention trigger
      }
      if (/\s/.test(ch)) break;
      i--;
    }
    setPopoverOpen(false);
    setTokenStart(null);
  }, [draft, ensureUsers]);

  useEffect(() => {
    refreshTokenFromCursor();
  }, [draft, refreshTokenFromCursor]);

  const filteredUsers = useMemo(() => {
    if (!popoverOpen) return [];
    const f = filter.trim();
    const base = f
      ? users.filter((u) => `${u.firstname} ${u.lastname || ''}`.toLowerCase().includes(f))
      : users;
    return base.slice(0, 8);
  }, [popoverOpen, filter, users]);

  const selectMention = useCallback(
    (u: ActiveUser) => {
      if (tokenStart == null || !textareaRef.current) return;
      const ta = textareaRef.current;
      const cursor = ta.selectionStart ?? draft.length;
      const fullName = `${u.firstname}${u.lastname ? ' ' + u.lastname : ''}`;
      const insertText = `@${fullName} `;
      const next = draft.slice(0, tokenStart) + insertText + draft.slice(cursor);
      setDraft(next);
      setMentions((prev) => (prev.some((m) => m.id === u.id) ? prev : [...prev, { id: u.id, name: fullName }]));
      setPopoverOpen(false);
      setTokenStart(null);
      requestAnimationFrame(() => {
        const pos = tokenStart + insertText.length;
        ta.focus();
        ta.setSelectionRange(pos, pos);
      });
    },
    [tokenStart, draft]
  );

  const removeMention = (id: string) => {
    setMentions((prev) => prev.filter((m) => m.id !== id));
  };

  const handlePost = useCallback(async () => {
    const text = draft.trim();
    if (!text || posting) return;
    setPosting(true);
    setPostError(null);
    try {
      const created = await holdWhile(messagesApi.create(entityType, entityId, {
        body: text,
        ...(mentions.length > 0 ? { mention_user_ids: mentions.map((m) => m.id) } : {}),
      }));
      setItems((prev) => [...prev, created]);
      setDraft('');
      setMentions([]);
    } catch (err: any) {
      setPostError(
        err?.data?.error?.message || err?.message || 'Could not post that. Try again in a moment.'
      );
    } finally {
      setPosting(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft, mentions, posting, entityType, entityId]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (popoverOpen && filteredUsers.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setHighlight((h) => Math.min(h + 1, filteredUsers.length - 1));
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setHighlight((h) => Math.max(h - 1, 0));
        return;
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        selectMention(filteredUsers[highlight]);
        return;
      }
      if (e.key === 'Escape') {
        setPopoverOpen(false);
        return;
      }
    }
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handlePost();
    }
    // Shift+Enter: default textarea behavior (newline).
  };

  const listboxIdRef = useRef<string>(`message-thread-mentions-${Math.random().toString(36).slice(2, 10)}`);
  const listboxId = listboxIdRef.current;
  const optionId = (i: number) => `${listboxId}-opt-${i}`;
  const showPopover = popoverOpen && filteredUsers.length > 0;

  // Below lg the host page has no room for a sidebar, so consumers used to wrap
  // this in `hidden lg:block` and the thread simply did not exist on a phone.
  // It now carries its own small-screen presentation: a floating bubble that
  // opens a sheet. Bottom-RIGHT on purpose — Flerpie's avatar sits bottom-left.
  //
  // The choice is made in JS rather than with two CSS-hidden copies: duplicating
  // the markup would mean two scroll containers and two textareas competing for
  // focus and autoscroll. One instance, one home.
  const commentCount = items.filter((i) => i.kind === 'comment').length;

  const stream = (
    <div ref={scrollRef} className={`overflow-y-auto px-4 py-1 ${isDesktop ? 'max-h-[28rem]' : 'flex-1 min-h-0'}`}>
        <FadeIn key={loading ? 'loading' : loadError ? 'error' : items.length === 0 ? 'empty' : 'thread'}>
        {loading ? (
          <SkeletonGroup label="Loading thread" className="space-y-3 py-4">
            {[0, 1, 2].map((n) => <Skeleton key={n} className="h-10 w-full" />)}
          </SkeletonGroup>
        ) : loadError ? (
          <Alert tone="error" variant="text" className="py-6">{loadError}</Alert>
        ) : items.length === 0 ? (
          <NoComments />
        ) : (
          items.map((item) =>
            item.kind === 'system' ? (
              <SystemEntry key={item.id} item={item} />
            ) : (
              <CommentEntry key={item.id} item={item} />
            )
          )
        )}
        </FadeIn>
    </div>
  );

  const composer = (
      <div className="p-3 pt-2">
        <Reveal show={mentions.length > 0}>
          <div className="mb-2 flex flex-wrap gap-1.5">
            {mentions.map((m) => (
              <span
                key={m.id}
                data-testid="mention-chip"
                className="inline-flex items-center gap-1 rounded-full bg-primary/15 py-0.5 pl-2.5 pr-1 text-xs font-medium text-primary-ink motion-safe:animate-menuIn"
              >
                @{m.name}
                <button
                  type="button"
                  onClick={() => removeMention(m.id)}
                  aria-label={`Remove mention of ${m.name}`}
                  className={`np-state inline-flex h-4 w-4 items-center justify-center rounded-full text-primary transition-[color,--np-layer] duration-quick ease-emphasized ${FOCUS}`}
                >
                  <IconClose className="h-3 w-3" />
                </button>
              </span>
            ))}
          </div>
        </Reveal>

        <div className="relative">
          <TextArea
            ref={textareaRef}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={handleKeyDown}
            onSelect={refreshTokenFromCursor}
            disabled={posting}
            rows={2}
            aria-label="Write a comment"
            placeholder="Write a comment. Type @ to mention someone."
            role="combobox"
            aria-expanded={showPopover}
            aria-controls={showPopover ? listboxId : undefined}
            aria-activedescendant={showPopover ? optionId(highlight) : undefined}
            aria-autocomplete="list"
            className="!resize-none"
          />
          {showPopover && (
            <ul
              id={listboxId}
              role="listbox"
              className="absolute bottom-full left-0 right-0 z-30 m-0 mb-1 max-h-56 list-none overflow-y-auto rounded-overlay bg-surface-container-high p-1 shadow-overlay origin-bottom motion-safe:animate-menuIn"
            >
              {filteredUsers.map((u, i) => (
                <li
                  key={u.id}
                  id={optionId(i)}
                  role="option"
                  aria-selected={i === highlight}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    selectMention(u);
                  }}
                  onMouseEnter={() => setHighlight(i)}
                  className={`flex min-h-8 cursor-pointer items-center gap-2 rounded-inner px-2 py-1 text-sm transition-[background-color,color] duration-quick ease-emphasized ${
                    i === highlight ? 'bg-primary/15 text-primary-ink' : 'text-on-surface hover:bg-surface-container-highest'
                  }`}
                >
                  <span className="font-medium">{u.firstname}</span>
                  {u.lastname && <span className={i === highlight ? 'text-primary-ink' : 'text-on-surface-variant'}>{u.lastname}</span>}
                </li>
              ))}
            </ul>
          )}
        </div>

        <Reveal show={!!postError}>
          <p className="m-0 mt-1.5 text-sm text-red-300">{postError}</p>
        </Reveal>

        <div className="mt-2 flex items-center justify-between gap-3">
          <span className="min-w-0 truncate text-xs text-on-surface-variant">Enter posts, Shift+Enter adds a line</span>
          <Button variant="filled" size="sm" onClick={handlePost} disabled={posting || !draft.trim()}>
            {posting ? 'Posting…' : 'Post'}
          </Button>
        </div>
      </div>
  );

  if (!isDesktop) {
    return (
      <>
        <button
          type="button"
          onClick={() => setSheetOpen(true)}
          aria-label={commentCount > 0 ? `Open activity, ${commentCount} comments` : 'Open activity'}
          className={`np-state np-state-on-primary fixed bottom-5 right-5 z-40 flex h-14 w-14 items-center justify-center rounded-overlay bg-primary text-on-primary shadow-overlay transition-[transform,--np-layer] duration-quick ease-emphasized ${PRESS} ${FOCUS}`}
        >
          <svg aria-hidden="true" focusable="false" className="h-6 w-6" viewBox="0 0 24 24" fill="none">
            <path
              fillRule="evenodd"
              d="M12 3.5c-5 0-9 3.6-9 8.1 0 2.1.9 4 2.4 5.5-.2 1.2-.8 2.3-1.6 3.1-.3.3-.1.8.3.8 1.9 0 3.5-.6 4.6-1.4 1 .3 2.1.5 3.3.5 5 0 9-3.6 9-8.1S17 3.5 12 3.5zM8 10.5a1 1 0 000 2h8a1 1 0 100-2H8zm0 3.5a1 1 0 100 2h5a1 1 0 100-2H8z"
              fill="currentColor"
            />
          </svg>
          <Appear
            show={commentCount > 0}
            className="absolute -right-1.5 -top-1.5 flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-surface px-1 text-[11px] font-bold tabular-nums text-primary ring-2 ring-surface"
          >
            {commentCount > 99 ? '99+' : commentCount}
          </Appear>
        </button>

        {sheet.mounted && (
          <div
            className={`fixed inset-0 z-50 flex flex-col bg-surface transition-[transform,opacity] motion-reduce:translate-y-0 motion-reduce:transition-[opacity] ${
              sheet.open && sheetOpen
                ? 'translate-y-0 opacity-100 duration-spring ease-spring'
                : 'translate-y-6 opacity-0 duration-exit ease-exit'
            }`}
          >
            <div className="flex shrink-0 items-center justify-between bg-surface-container-low px-4 py-2">
              <h2 className="m-0 text-base font-semibold text-on-surface">Activity</h2>
              <IconButton label="Close activity" onClick={() => setSheetOpen(false)} className="-mr-1.5">
                <IconClose className="h-5 w-5" />
              </IconButton>
            </div>
            {stream}
            {composer}
          </div>
        )}
      </>
    );
  }

  return (
    <section className="flex flex-col rounded-panel bg-surface-container-low">
      <div className="px-4 pb-1 pt-4">
        <h2 className="m-0 text-base font-semibold leading-6 text-on-surface">Activity</h2>
      </div>
      {stream}
      {composer}
    </section>
  );
};

export default MessageThread;
